import React, { Suspense, useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";
import { SpotlightIcon } from "./spotlightIcons";
import { copyTextToClipboard } from "../lib/clipboard";
import type { SpotlightSettingsState } from "../hooks/useSpotlightSettings";
import type { SpotlightTheme } from "../types/spotlight";
import "./SpotlightSettings.css";
import { t } from "../i18n";

// Fan's Spotlight (founder, 2026-10-19): the QR Code tab's fourth round
// button opens this sheet. At the top, one line and a short demo night that
// plays on its own say what it is (many talent find it just by exploring);
// then the switch, the screen link (mostly hidden, as Railway does: what
// matters is Copy), where to open it, Style, Fun icons and Show price paid
// (founder, 1.5.2 b49), which change the demo the moment they are tapped. The demo is drawn by the screen's own
// code and loaded only when the sheet opens.
const SpotlightPreview = React.lazy(() => import("./SpotlightPreview"));

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="8" width="12" height="12" rx="2.5" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
const ResetIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12a8 8 0 1 0 2.4-5.7" />
    <path d="M4 4.5v4.2h4.2" />
  </svg>
);
const lineIcon = (children: React.ReactNode) => (
  <span className="profile-setting-ic" aria-hidden="true">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </span>
);
/** A party hat: the fun icons. */
const FUN_ICON = lineIcon(
  <>
    <path d="M12 3.2 18.6 19.4H5.4Z" />
    <path d="M8.6 11.8l6.2-1.9M7.1 15.7l8.8-2.7" />
    <path d="M4.5 19.6h15" />
  </>,
);

/** A price tag: "Show price paid". */
const PRICE_ICON = lineIcon(
  <>
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
    <circle cx="7.5" cy="7.5" r="1.5" />
  </>,
);

/** The link as people read it: requesta.app/s/<code>. */
export const shortLink = (link: string) => link.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
/** The link with its private code hidden: requesta.app/s/••••••. */
export const maskedLink = (link: string) => shortLink(link).replace(/\/s\/[^/?#]+$/, "/s/••••••");

/**
 * The Fan's Spotlight sheet. Nothing until the server has answered (an older
 * server: the QR tab shows no button either). scanUrl = the talent's fan link,
 * for the demo's Scan state.
 */
export function SpotlightSheet({ open, onClose, state, currency, scanUrl }: { open: boolean; onClose: () => void; state: SpotlightSettingsState; currency?: string | null; scanUrl?: string | null }) {
  const { settings, failed, busy, save, resetLink } = state;
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);
  useEffect(() => {
    if (!open) setReveal(false); // the link is hidden again every time the sheet opens
  }, [open]);
  if (!settings) return null;
  const on = settings.enabled;
  const theme = settings.theme;
  const seg = (v: SpotlightTheme) => (
    <button
      key={v}
      type="button"
      role="radio"
      aria-checked={theme === v}
      className={`profile-display-btn${theme === v ? " profile-display-btn--active" : ""}`}
      onClick={() => theme !== v && save({ theme: v })}
    >
      {v === "minimal" ? t("spotlightSheet.minimal") : t("spotlightSheet.colorful")}
    </button>
  );
  return (
    <BottomSheet open={open} title={t("spotlightSheet.title")} icon={<SpotlightIcon size={18} />} onClose={onClose} className="sp-sheet">
      <p className="sp-intro">{t("spotlightSheet.intro")}</p>
      <div className="sp-prev-scr">
        {open && (
          <Suspense fallback={null}>
            <SpotlightPreview minimal={theme === "minimal"} icons={settings.icons} prices={settings.prices !== false} currency={currency || "usd"} scanUrl={scanUrl} label={t("spotlightSheet.previewAria")} />
          </Suspense>
        )}
      </div>
      <div className="profile-settings profile-group-card sp-card sp-main">
        <div className="profile-setting-row">
          <span className="profile-setting-ic" aria-hidden="true">
            <SpotlightIcon size={22} />
          </span>
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("spotlightSheet.switch")}</div>
            <div className="profile-setting-sub">{on ? t("spotlightSheet.subOn") : t("spotlightSheet.subOff")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={t("spotlightSheet.switch")}
            className={`profile-toggle ${on ? "on" : ""}`}
            onClick={() => save({ enabled: !on })}
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        {on && settings.link && (
          <div className="sp-linkbox">
            <div className="sp-field">
              <button
                type="button"
                className="sp-field-link"
                aria-label={reveal ? t("spotlightSheet.hideLink") : t("spotlightSheet.showLink")}
                onClick={() => setReveal((r) => !r)}
              >
                {reveal ? shortLink(settings.link) : maskedLink(settings.link)}
              </button>
              <button
                type="button"
                className="sp-copy"
                onClick={() => copyTextToClipboard(settings.link as string).then(() => setCopied(true))}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied ? t("spotlightSheet.copied") : t("spotlightSheet.copy")}
              </button>
            </div>
            <p className="sp-devices">{t("spotlightSheet.devices")}</p>
            <button
              type="button"
              className="sp-reset"
              disabled={busy}
              onClick={() => {
                if (window.confirm(t("spotlightSheet.resetConfirm"))) resetLink();
              }}
            >
              <ResetIcon />
              {t("spotlightSheet.reset")}
            </button>
          </div>
        )}
      </div>
      {/* The style box gets a heading (founder, 1.5.2 b49 test: "it's just a box without a header"). */}
      <h3 className="sp-section-title">{t("spotlightSheet.settingsTitle")}</h3>
      <div className="profile-settings profile-group-card sp-card sp-card--titled">
        <div className="profile-display-toggle sp-seg" role="radiogroup" aria-label={t("spotlightSheet.style")}>
          {seg("colorful")}
          {seg("minimal")}
        </div>
        <div className="sp-sub">{theme === "minimal" ? t("spotlightSheet.minimalSub") : t("spotlightSheet.colorfulSub")}</div>
        <div className="profile-setting-row sp-icons-row">
          {FUN_ICON}
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("spotlightSheet.funIcons")}</div>
            <div className="profile-setting-sub">{t("spotlightSheet.funIconsSub")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={settings.icons}
            aria-label={t("spotlightSheet.funIcons")}
            className={`profile-toggle ${settings.icons ? "on" : ""}`}
            onClick={() => save({ icons: !settings.icons })}
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        {/* Only when the server knows the setting (an older one would refuse it). */}
        {typeof settings.prices === "boolean" && (
          <div className="profile-setting-row sp-icons-row">
            {PRICE_ICON}
            <div className="profile-setting-text">
              <div className="profile-setting-label">{t("spotlightSheet.prices")}</div>
              <div className="profile-setting-sub">{t("spotlightSheet.pricesSub")}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.prices}
              aria-label={t("spotlightSheet.prices")}
              className={`profile-toggle ${settings.prices ? "on" : ""}`}
              onClick={() => save({ prices: !settings.prices })}
            >
              <span className="profile-toggle-knob" />
            </button>
          </div>
        )}
      </div>
      {failed && <span className="field-status field-status--error">{t("spotlightSheet.saveFailed")}</span>}
    </BottomSheet>
  );
}
